import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { useLocale } from "../hooks/useLocale";
import { lineStock, stockDate, stockNumber } from "../utils/lineStock";
import { StockAdditionalDetails, StockContextNote, StockReason } from "./StockAdditionalDetails";
import { stockCustomerOrders, stockFields } from "../utils/stockSupply";
import { ProductThumbnail } from "./ProductThumbnail";
import { StockCustomerOrders } from "./StockCustomerOrders";

const colors = { green: "#218447", amber: "#b87300", red: "#c53636" };
const stockLoadingSx = {
  bgcolor: "rgba(167, 139, 250, 0.22)",
  "&::after": { background: "linear-gradient(90deg, transparent, rgba(167, 139, 250, 0.55), transparent)" },
};

/**
 * `wide`: en PC la ventana de búsqueda se ensancha en lugar de hacer scroll. A la izquierda, el
 * resumen y los compromisos; a la derecha, las entradas previstas de proveedor.
 * `"columns"`: los almacenes van a la derecha y los pedidos de clientes, si los hay, debajo.
 * `"beside"`: los pedidos de clientes van a la derecha (más ancha) y los almacenes a la izquierda.
 */
export function ProductStockSearchResult({ result, wide = false }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { data, loading, error, retry } = result;
  if (loading) return <Box role="status" aria-label={t("stock.checking")} sx={{ p: 2 }}>
    <Skeleton animation="wave" width="65%" height={28} sx={stockLoadingSx} />
    <Skeleton animation="wave" width="90%" sx={stockLoadingSx} />
    {[0, 1, 2, 3].map(index => <Skeleton key={index} animation="wave" height={30} sx={stockLoadingSx} />)}
  </Box>;
  if (error || data?.status !== "resolved") return <Box sx={{ p: 2 }} role="status">
    <Typography variant="body2">{t(`stockSearch.${error ? "unavailable" : data?.status ?? "unavailable"}`)}</Typography>
    {(error || data?.status === "unavailable") && <Button size="small" onClick={retry} sx={{ mt: 1 }}>{t("common.retry")}</Button>}
  </Box>;
  const { product } = data;
  const stock = lineStock({ ...product, stock: data.stock, stockPending: data.pending });
  const checkedAt = stockDate(stock.checked_at, locale);
  const columns = wide ? { display: "grid", alignItems: "start",
    gridTemplateColumns: wide === "beside" ? "minmax(0, 0.75fr) minmax(0, 1.25fr)" : "minmax(0, 1fr) minmax(0, 1fr)" } : {};
  return <Box aria-live="polite">
    <Box sx={columns}>
    <Box sx={wide ? { borderRight: "1px solid", borderColor: "divider", pb: 1 } : {}}>
    <Box sx={{ p: 2, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <ProductThumbnail reference={product.ref} size={{ xs: 64, sm: 72 }} />
      <Box sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{[...new Set([product.code, product.ref].filter(Boolean))].join(" · ")}</Typography>
        {product.brand && <Typography variant="caption" color="text.secondary">{product.brand}</Typography>}
        <Typography variant="body2" sx={{ mt: 0.75 }}>{product.description}</Typography>
      </Box>
      {stock.indicator && <Box role="img" aria-label={t(`stock.${stock.indicator}`)} sx={{ width: 14, height: 14,
        mt: 0.5, flexShrink: 0, borderRadius: "50%", bgcolor: colors[stock.indicator] }} />}
    </Box>
    <Box sx={{ px: 2 }}><StockContextNote stock={stock} locale={locale} /></Box>
    {stock.status === "ok" ? <Table size="small" aria-label={t("stock.column")} sx={{ "& .MuiTableCell-root": { px: 2, py: 1, fontSize: 12 } }}>
      <TableBody>{stockFields(stock).map(([field, label]) => <TableRow key={field} sx={field === "available" ? { bgcolor: "action.hover" } : {}}>
        <TableCell component="th" scope="row" sx={{ fontWeight: field === "available" ? 700 : 400 }}>{t(label)}</TableCell>
        <TableCell align="right" data-stock-value={field} sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{stockNumber(stock[field], locale) ?? t("stock.unknown")}</TableCell>
      </TableRow>)}</TableBody>
    </Table> : <Box role="status" sx={{ px: 2, pb: 2 }}>
      <Typography variant="body2" color="text.secondary">{t(`stock.${stock.status}`)}</Typography>
      <StockReason stock={stock} />
      {data.pending && <Skeleton animation="wave" height={24} sx={stockLoadingSx} />}
    </Box>}
    {wide && <StockAdditionalDetails stock={stock} locale={locale} sx={{ mx: 2, mb: 2 }} showWarehouses={wide === "beside"} showArrivals={false} showExplanation={false} />}
    </Box>
    {/* Dos columnas: los compromisos siguen al resumen; las entradas previstas de proveedor
        (y la explicación) pasan a la columna derecha. */}
    <Box>
    <StockAdditionalDetails stock={stock} locale={locale} sx={{ mx: 2, mb: 2 }}
      {...(wide ? { showCommitments: false, showWarehouses: wide !== "beside" } : {})} />
    {(!wide || wide === "beside") && <StockCustomerOrders stock={stock} locale={locale} sx={{ mx: 2, mb: 2 }} />}
    </Box>
    </Box>
    {/* Los pedidos de clientes ocupan todo el ancho para que se vean todas sus columnas. */}
    {wide === "columns" && stockCustomerOrders(stock) && <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
      <StockCustomerOrders stock={stock} locale={locale} sx={{ mx: 2, mb: 2 }} />
    </Box>}
    {/* En ancho, aviso y hora de consulta en una sola línea. */}
    <Box sx={{ px: 2, py: 1.25, borderTop: "1px solid", borderColor: "divider", color: "text.secondary",
      ...(wide ? { display: "flex", flexDirection: "row-reverse", justifyContent: "space-between", alignItems: "baseline", gap: 2 } : {}) }}>
      {!data.pending && <Typography variant="caption" component="p" sx={{ m: 0, mb: wide ? 0 : 0.75, textAlign: "right", fontSize: 10.5 }}>{t("stock.checkedAt", { value: checkedAt ?? t("stock.unknown") })}</Typography>}
      <Typography variant="caption" sx={{ display: "block", fontSize: 10.5 }}>{t("stock.informational")}</Typography>
    </Box>
  </Box>;
}
